/**
 * Página de detalle de una noticia específica
 * Estilo Awwwards con animaciones GSAP y diseño inmersivo
 * Muestra el contenido completo de una noticia/actualidad
 */

import React, { useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import useFormatDate from '../hooks/useFormatDate';
import { useParams } from 'react-router-dom';
import LocalizedNavigate from '../components/common/LocalizedNavigate';
import LocalizedLink from '../components/common/LocalizedLink';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import ImmersiveLayout from '../components/layout/ImmersiveLayout';
import { AnimatedSection } from '../components/animations';
import { getNewsBySlug, getCategoryText, getLatestNews } from '../data/newsData';
import useLocalizeContent from '../hooks/useLocalizeContent';

gsap.registerPlugin(ScrollTrigger);

/**
 * Página de detalle de noticia individual con estilo Awwwards
 * @returns {JSX.Element} Página de detalle de noticia inmersiva
 */
const NewsDetailPage = () => {
  const { t } = useTranslation('news');
  const formatDate = useFormatDate();
  const { slug } = useParams();
  const { localize } = useLocalizeContent();
  // La búsqueda por slug usa el dato original (los slugs no se traducen);
  // después se resuelve el texto al idioma activo.
  const newsItem = localize(getNewsBySlug(slug));
  const relatedNews = localize(getLatestNews(3).filter(n => n.slug !== slug).slice(0, 2));
  
  const heroRef = useRef(null);
  const titleRef = useRef(null);
  const contentRef = useRef(null);

  // Scroll al inicio cuando cambia la noticia
  useEffect(() => {
    window.scrollTo(0, 0);
    document.documentElement.scrollTop = 0;
    document.body.scrollTop = 0;
    if (window.__lenis) {
      window.__lenis.scrollTo(0, { immediate: true });
    }
  }, [slug]);

  // Animación del hero al cargar
  useEffect(() => {
    const hero = heroRef.current;
    const title = titleRef.current;
    if (!hero || !title) return;

    const tl = gsap.timeline();
    
    // Animación de entrada del título carácter por carácter
    const chars = title.querySelectorAll('.char');
    tl.fromTo(chars,
      { y: 100, opacity: 0 },
      { 
        y: 0, 
        opacity: 1, 
        duration: 0.8, 
        stagger: 0.02,
        ease: 'power3.out' 
      }
    );

    // Parallax del hero
    gsap.to(hero.querySelector('.hero-image'), {
      yPercent: 30,
      ease: 'none',
      scrollTrigger: {
        trigger: hero,
        start: 'top top',
        end: 'bottom top',
        scrub: true,
      },
    });

    return () => {
      tl.kill();
      ScrollTrigger.getAll().forEach(trigger => trigger.kill());
    };
  }, [slug]);

  // Si no se encuentra la noticia, redirigir
  if (!newsItem) {
    return <LocalizedNavigate to="/noticias" replace />;
  }

  // Función para dividir el título en palabras (evita romper palabras a nivel de letras)
  const splitTitle = (text) => {
    return text.split(' ').map((word, index, array) => (
      <span key={index} className="char inline-block whitespace-nowrap">
        {word}{index < array.length - 1 ? '\u00A0' : ''}
      </span>
    ));
  };

  return (
    <ImmersiveLayout>
      {/* 
        =============================================
        1. HERO INMERSIVO - Pantalla completa
        =============================================
      */}
      <section 
        ref={heroRef}
        className="relative min-h-screen flex items-end justify-start overflow-hidden"
      >
        {/* Imagen de fondo con parallax */}
        <div className="hero-image absolute inset-0">
          <div className="w-full h-[120%] bg-gradient-to-br from-intercasa-dark via-gray-900 to-intercasa-dark">
            {newsItem.imageUrl ? (
              <img 
                src={newsItem.imageUrl} 
                alt={newsItem.title}
                className="w-full h-full object-cover opacity-60"
                onError={(e) => {
                  e.target.style.display = 'none';
                }}
              />
            ) : (
              <div className="absolute inset-0 opacity-[0.03]" style={{
                backgroundImage: 'radial-gradient(circle at 1px 1px, white 1px, transparent 0)',
                backgroundSize: '40px 40px'
              }}></div>
            )}
          </div>
          {/* Overlay oscuro */}
          <div className="absolute inset-0 bg-gradient-to-t from-intercasa-dark via-intercasa-dark/70 to-transparent"></div>
        </div>
        
        {/* Elementos decorativos */}
        <div className="absolute top-1/3 right-0 w-[600px] h-[600px] bg-intercasa-red/10 rounded-full blur-3xl"></div>
        <div className="absolute bottom-0 left-0 w-[400px] h-[400px] bg-intercasa-yellow/5 rounded-full blur-3xl"></div>
        
        {/* Contenido del hero */}
        <div className="container mx-auto px-4 md:px-8 relative z-10 pb-20 md:pb-32">
          <div className="max-w-5xl">
            {/* Metadatos */}
            <div className="flex flex-wrap items-center gap-4 mb-8">
              {/* Fecha */}
              <span className="inline-flex items-center px-4 py-2 bg-white/10 backdrop-blur-sm text-white/80 text-sm font-medium rounded-full border border-white/10">
                <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                </svg>
                {formatDate(newsItem.date)}
              </span>
            </div>
            
            {/* Título animado */}
            <h1 
              ref={titleRef}
              className="font-baskerville font-bold text-white text-4xl md:text-5xl lg:text-6xl xl:text-7xl leading-[1.1] mb-8"
            >
              {splitTitle(newsItem.title)}
            </h1>
            
            {/* Resumen */}
            <p className="text-xl md:text-2xl text-white/70 max-w-3xl font-light leading-relaxed">
              {newsItem.summary}
            </p>
          </div>
        </div>
      </section>

      {/* 
        =============================================
        2. CONTENIDO DEL ARTÍCULO
        =============================================
      */}
      <section ref={contentRef} className="bg-white py-20 md:py-32">
        <div className="container mx-auto px-4 md:px-8">
          <div className="max-w-3xl mx-auto">
            {/* Contenido principal con animaciones */}
            <AnimatedSection animation="fadeUp">
              <div 
                className="prose prose-lg max-w-none
                  prose-headings:font-baskerville prose-headings:text-intercasa-dark prose-headings:font-bold
                  prose-p:text-intercasa-dark/70 prose-p:leading-relaxed prose-p:text-lg prose-p:mb-6 [&>p+p]:mt-6
                  prose-strong:text-intercasa-dark prose-strong:font-semibold
                  prose-a:text-intercasa-red prose-a:no-underline hover:prose-a:underline
                  prose-ul:text-intercasa-dark/70 prose-li:mb-3 prose-li:text-lg
                  prose-blockquote:border-l-4 prose-blockquote:border-intercasa-red prose-blockquote:pl-6 prose-blockquote:italic prose-blockquote:text-intercasa-dark/60
                  space-y-6"
                dangerouslySetInnerHTML={{ __html: newsItem.content }}
              />
            </AnimatedSection>
          </div>
        </div>
      </section>

      {/* 
        =============================================
        3. GALERÍA DE IMÁGENES (si existe) - Estilo Awwwards
        =============================================
      */}
      {newsItem.gallery && newsItem.gallery.length > 0 && (
        <section className="bg-intercasa-dark py-20 md:py-32 relative overflow-hidden">
          {/* Elementos decorativos */}
          <div className="absolute top-0 left-0 w-[400px] h-[400px] bg-intercasa-red/5 rounded-full blur-3xl"></div>
          <div className="absolute bottom-0 right-0 w-[300px] h-[300px] bg-intercasa-yellow/5 rounded-full blur-3xl"></div>
          
          <div className="container mx-auto px-4 md:px-8 relative z-10">
            {/* Layout de galería asimétrico estilo Awwwards */}
            <div className="grid grid-cols-12 gap-4 md:gap-6">
              {/* Imagen principal grande */}
              {newsItem.gallery[0] && (
                <AnimatedSection animation="fadeUp" className="col-span-12 md:col-span-8">
                  <div className="group relative aspect-[16/10] rounded-2xl overflow-hidden cursor-pointer">
                    <img
                      src={newsItem.gallery[0]}
                      alt={t('detail.galleryAlt', { title: newsItem.title })}
                      className="w-full h-full object-cover transition-transform duration-1000 group-hover:scale-105"
                    />
                    {/* Overlay con efecto */}
                    <div className="absolute inset-0 bg-gradient-to-t from-intercasa-dark/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
                  </div>
                </AnimatedSection>
              )}
              
              {/* Columna derecha con 2 imágenes apiladas */}
              <div className="col-span-12 md:col-span-4 flex flex-col gap-4 md:gap-6">
                {newsItem.gallery[1] && (
                  <AnimatedSection animation="fadeUp" delay={0.1} className="flex-1">
                    <div className="group relative h-full min-h-[200px] rounded-2xl overflow-hidden cursor-pointer">
                      <img
                        src={newsItem.gallery[1]}
                        alt={t('detail.galleryAlt', { title: newsItem.title })}
                        className="w-full h-full object-cover transition-transform duration-1000 group-hover:scale-105"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-intercasa-dark/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
                    </div>
                  </AnimatedSection>
                )}
                
                {newsItem.gallery[2] && (
                  <AnimatedSection animation="fadeUp" delay={0.2} className="flex-1">
                    <div className="group relative h-full min-h-[200px] rounded-2xl overflow-hidden cursor-pointer">
                      <img
                        src={newsItem.gallery[2]}
                        alt={t('detail.galleryAlt', { title: newsItem.title })}
                        className="w-full h-full object-cover transition-transform duration-1000 group-hover:scale-105"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-intercasa-dark/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
                    </div>
                  </AnimatedSection>
                )}
              </div>
            </div>
          </div>
        </section>
      )}

      {/* 
        =============================================
        4. NOTICIAS RELACIONADAS
        =============================================
      */}
      {relatedNews.length > 0 && (
        <section className="bg-intercasa-bone py-20 md:py-32">
          <div className="container mx-auto px-4 md:px-8">
            <AnimatedSection animation="fadeUp" className="text-center mb-16">
              <span className="font-lato text-intercasa-red text-sm uppercase tracking-widest mb-4 block">
                {t('detail.keepReading')}
              </span>
              <h2 className="font-baskerville font-bold text-intercasa-dark text-3xl md:text-4xl lg:text-5xl">
                {t('detail.moreNews')}
              </h2>
            </AnimatedSection>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-4xl mx-auto">
              {relatedNews.map((related, index) => (
                <AnimatedSection key={related.id} animation="fadeUp" delay={index * 0.1}>
                  <LocalizedLink
                    to={`/noticias/${related.slug}`}
                    className="group relative bg-white rounded-2xl overflow-hidden shadow-sm hover:shadow-xl transition-all duration-500"
                  >
                    {/* Imagen */}
                    <div className="relative aspect-[4/3] overflow-hidden bg-gradient-to-br from-gray-100 to-gray-200">
                      <div className="absolute inset-0 transition-transform duration-700 group-hover:scale-105">
                        {related.imageUrl ? (
                          <img 
                            src={related.imageUrl} 
                            alt={related.title}
                            className="w-full h-full object-cover"
                          />
                        ) : (
                          <div className="w-full h-full bg-gradient-to-br from-intercasa-red/10 to-intercasa-red/5 flex items-center justify-center">
                            <svg className="w-16 h-16 text-intercasa-red/30" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M4 3a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V5a2 2 0 00-2-2H4zm12 12H4l4-8 3 6 2-4 3 6z" clipRule="evenodd" />
                            </svg>
                          </div>
                        )}
                      </div>

                      {/* Badge de fecha */}
                      <div className="absolute top-4 left-4 z-10">
                        <span className="inline-block px-3 py-1 bg-white/90 backdrop-blur-sm text-intercasa-dark text-xs font-bold rounded-full">
                          {formatDate(related.date)}
                        </span>
                      </div>
                    </div>

                    {/* Contenido */}
                    <div className="p-6">
                      {/* Categoría */}
                      {related.category && (
                        <span className="inline-block text-xs text-intercasa-red font-bold uppercase tracking-wider mb-3">
                          {getCategoryText(related.category)}
                        </span>
                      )}
                      
                      <h3 className="font-baskerville font-bold text-xl text-intercasa-dark mb-3 group-hover:text-intercasa-red transition-colors duration-300">
                        {related.title}
                      </h3>
                      
                      {related.summary && (
                        <p className="text-intercasa-gray text-sm mb-4">
                          {related.summary.split('.')[0]}.
                        </p>
                      )}

                      {/* Link */}
                      <span className="inline-flex items-center text-intercasa-red font-semibold text-sm group-hover:gap-3 gap-2 transition-all duration-300">
                        {t('list.readMore')}
                        <svg className="w-4 h-4 transform group-hover:translate-x-1 transition-transform duration-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
                        </svg>
                      </span>
                    </div>

                    {/* Línea inferior animada */}
                    <div className="absolute bottom-0 left-0 w-full h-1 bg-intercasa-red transform scale-x-0 group-hover:scale-x-100 transition-transform duration-500 origin-left"></div>
                  </LocalizedLink>
                </AnimatedSection>
              ))}
            </div>

            {/* Botón ver todas */}
            <AnimatedSection animation="fadeUp" delay={0.3} className="text-center mt-12">
              <LocalizedLink 
                to="/noticias"
                className="group inline-flex items-center bg-intercasa-dark text-white font-semibold px-8 py-4 rounded-full hover:bg-intercasa-red transition-all duration-300"
              >
                {t('detail.viewMore')}
                <svg className="w-5 h-5 ml-2 transform group-hover:translate-x-1 transition-transform duration-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
                </svg>
              </LocalizedLink>
            </AnimatedSection>
          </div>
        </section>
      )}
    </ImmersiveLayout>
  );
};

export default NewsDetailPage;